import React, { createContext, useContext, useState, useEffect } from 'react';

export type SafetyMode = 'simulated' | 'anonymized';

interface SafetyModeContextValue {
  mode: SafetyMode;
  confirmed: boolean;
  setMode: (mode: SafetyMode) => void;
  setConfirmed: (confirmed: boolean) => void;
}

const SafetyModeContext = createContext<SafetyModeContextValue | null>(null);

const getInitialMode = (): SafetyMode => {
  if (typeof window === 'undefined') return 'simulated';
  const stored = window.localStorage.getItem('lyra-safety-mode') as SafetyMode | null;
  return stored === 'anonymized' ? 'anonymized' : 'simulated';
};

const getInitialConfirmation = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('lyra-safety-confirmed') === 'true';
};

export const SafetyModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<SafetyMode>(getInitialMode);
  const [confirmed, setConfirmed] = useState<boolean>(getInitialConfirmation);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('lyra-safety-mode', mode);
    if (mode === 'simulated') {
      setConfirmed(false);
      window.localStorage.removeItem('lyra-safety-confirmed');
    }
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (mode === 'anonymized') {
      window.localStorage.setItem('lyra-safety-confirmed', JSON.stringify(confirmed));
    }
  }, [mode, confirmed]);

  return (
    <SafetyModeContext.Provider value={{ mode, confirmed, setMode, setConfirmed }}>
      {children}
    </SafetyModeContext.Provider>
  );
};

export const useSafetyMode = () => {
  const context = useContext(SafetyModeContext);
  if (!context) {
    throw new Error('useSafetyMode must be used within a SafetyModeProvider');
  }
  return context;
};
